import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Plus, Trash2, Save, X, GripVertical } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEditMode } from "@/contexts/EditModeContext";
import { Label } from "@/components/ui/label";

interface TimelineStep {
  icon?: string;
  duration: string;
  title: string;
  description: string;
}

interface SetupInfo {
  setup_fee?: number;
  days_to_live?: number;
  timeline: TimelineStep[];
}

interface EditableSetupInfoProps {
  setupInfo: SetupInfo | null;
  onSave: (info: SetupInfo) => Promise<void>;
}

export const EditableSetupInfo = ({ setupInfo, onSave }: EditableSetupInfoProps) => {
  const { isEditor } = useAuth();
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [editedInfo, setEditedInfo] = useState<SetupInfo>(
    setupInfo && setupInfo.timeline ? setupInfo : { timeline: [] }
  );
  const [isSaving, setIsSaving] = useState(false);

  console.log('⏱️ EditableSetupInfo render:', {
    isEditor,
    isEditMode,
    hasSetupInfo: !!setupInfo,
    timelineSteps: setupInfo?.timeline?.length || 0,
    isEditing
  });

  if (!isEditor || !isEditMode) {
    console.log('❌ Not showing EditableSetupInfo:', { isEditor, isEditMode });
    return null;
  }

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedInfo);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedInfo(setupInfo && setupInfo.timeline ? setupInfo : { timeline: [] });
    setIsEditing(false);
  };

  const handleAddStep = () => {
    setEditedInfo({
      ...editedInfo,
      timeline: [...editedInfo.timeline, { duration: '', title: '', description: '' }]
    });
  };

  const handleRemoveStep = (index: number) => {
    setEditedInfo({
      ...editedInfo,
      timeline: editedInfo.timeline.filter((_, i) => i !== index)
    });
  };

  const handleUpdateStep = (index: number, field: keyof TimelineStep, value: string) => {
    const updated = [...editedInfo.timeline];
    updated[index] = { ...updated[index], [field]: value };
    setEditedInfo({ ...editedInfo, timeline: updated });
  };

  if (isEditing) {
    return (
      <Card className="p-6 border-dashed border-2 border-primary/50 bg-primary/5">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Edit Setup Timeline</h3>
            <div className="flex gap-2">
              <Button onClick={handleSave} size="sm" disabled={isSaving}>
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button onClick={handleCancel} size="sm" variant="outline">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <Label>Setup Fee ($)</Label>
              <Input
                type="number"
                placeholder="0"
                value={editedInfo.setup_fee || ''}
                onChange={(e) => setEditedInfo({ ...editedInfo, setup_fee: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Days to Go Live</Label>
              <Input
                type="number"
                placeholder="30"
                value={editedInfo.days_to_live || ''}
                onChange={(e) => setEditedInfo({ ...editedInfo, days_to_live: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Timeline Steps</h4>
              <Button onClick={handleAddStep} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Step
              </Button>
            </div>

            {editedInfo.timeline.map((step, index) => (
              <Card key={index} className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Step {index + 1}</span>
                  </div>
                  <Button
                    onClick={() => handleRemoveStep(index)}
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Duration (e.g., 5 days)"
                    value={step.duration}
                    onChange={(e) => handleUpdateStep(index, 'duration', e.target.value)}
                  />
                  <Input
                    placeholder="Icon (emoji or text)"
                    value={step.icon || ''}
                    onChange={(e) => handleUpdateStep(index, 'icon', e.target.value)}
                  />
                </div>
                <Input
                  placeholder="Step Title"
                  value={step.title}
                  onChange={(e) => handleUpdateStep(index, 'title', e.target.value)}
                />
                <Textarea
                  placeholder="Step Description"
                  value={step.description}
                  onChange={(e) => handleUpdateStep(index, 'description', e.target.value)}
                  rows={2}
                />
              </Card>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-dashed border-2 border-orange-300 bg-orange-50 dark:bg-orange-950/20">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center">
          <Pencil className="w-8 h-8 text-orange-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Setup & Timeline</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {setupInfo?.timeline && setupInfo.timeline.length > 0
              ? `${setupInfo.timeline.length} step${setupInfo.timeline.length !== 1 ? 's' : ''} configured. Click below to edit.`
              : "No setup timeline yet. Add setup fees, timeline steps, and go-live estimates to help customers understand your onboarding process."
            }
          </p>
        </div>
        <Button
          onClick={() => setIsEditing(true)}
          size="default"
          variant="default"
        >
          <Pencil className="w-4 h-4 mr-2" />
          {setupInfo?.timeline && setupInfo.timeline.length > 0 ? 'Edit Setup Timeline' : 'Add Setup Timeline'}
        </Button>
      </div>
    </Card>
  );
};
